/**
 * Sign-out orchestration (ENG-2907), extracted from client.ts.
 *
 * `client.ts` sits at the repo's 500-effective-line ceiling, and T8b (the
 * `AuthProvider` sign-out latch) + T8c (the GET /userinfo confirmation probe,
 * the pre-wipe `held` capture and the `AbortSignal` plumbing) reinstate code in
 * exactly this window — none of which fits inside `client.ts`. This module owns
 * the orchestration so that feature work lands here, in a smaller file, rather
 * than in a security-sensitive diff against an already-full one.
 *
 * The sign-out semantics remain the T3 contract: `POST /logout` -> classify the
 * status -> UNCONDITIONAL local wipe -> UNCONDITIONAL navigate to postLogoutUri.
 * ENG-2943 bounds the local wipe, and ENG-2954 adds a contained observation hook
 * between teardown and navigation. It NEVER throws and the local wipe always
 * completes (F3). The GET /userinfo probe and verdict-gated navigation are still
 * deferred to T8c; this always emits `probe: 'not_run'` on the signed_out arm.
 *
 * The stateful client owns the session variables and the in-memory teardown, so
 * this module takes them as `SignOutFlowDeps` rather than reaching back into
 * client.ts — that keeps the dependency edge one-way (client.ts -> this module)
 * and out of the `config -> client -> config` cycle #1767 removed.
 */
import type { UserManager } from "oidc-client-ts";
import { safeWarn, type AuthConfig } from "./config.js";
import type { SilentRefreshScheduler } from "./silent-refresh.js";
import { classifySignOutResponse, type SignOutResult } from "./sign-out.js";

export interface SignOutOptions {
  /**
   * ENG-2907 — what initiated the sign-out. Purely a copy hint for the
   * failure surface (T8's SignOutStatusScreen addresses the NEXT person at
   * the device differently for an idle timeout than for a deliberate "Sign
   * out" click). Has no effect on the sign-out flow itself.
   */
  trigger?: "user" | "idle";
  /**
   * Best-effort observation hook invoked after local teardown and immediately
   * before navigation when the server sign-out result is unsuccessful. This is
   * intentionally synchronous so a beacon-capable sink can enqueue telemetry
   * before the page leaves. A throwing hook is contained and cannot change the
   * verdict, teardown, or navigation behavior.
   *
   * Contract for implementers:
   *   - It receives a FROZEN COPY of the result, one per observer. Mutating it
   *     throws (strict mode) and is contained; it can change neither what
   *     signOut() returns nor what any other observer sees.
   *   - It MUST be synchronous, cheap and non-blocking (no awaits): it runs on
   *     the sign-out tail between the memo clear and navigation, so any work
   *     here delays the teardown navigation. A returned promise is not awaited.
   *   - It MUST NOT call signOut(). The memo is already cleared when it runs,
   *     so a re-entrant call would start a second POST /logout flow.
   */
  onFailureBeforeNavigate?: (
    failure: Extract<SignOutResult, { verdict: "not_signed_out" }>,
  ) => void;
}

/**
 * Everything the sign-out flow needs from the owning AuthClient. Passed in
 * (rather than importing client.ts) so the edge stays one-way: client.ts builds
 * these from its own closure state and hands them over.
 */
export interface SignOutFlowDeps {
  /** The client's silent-refresh scheduler; sign-out cancels it first. */
  scheduler: Pick<SilentRefreshScheduler, "cancel">;
  /** The client's authFetch — pins this app's audience + credentials. */
  authFetch: (path: string, init?: RequestInit) => Promise<Response>;
  /** oidc-client-ts user store; the wipe removes the cached OIDC user. */
  userManager: Pick<UserManager, "removeUser">;
  /** Resolved config; only `postLogoutUri` (sanitised at construction) is read. */
  config: Pick<AuthConfig, "postLogoutUri">;
  /**
   * Clear the client's IN-MEMORY session (currentSession / currentAccessToken)
   * and emit the resulting `null` session to listeners. Those variables live in
   * client.ts's closure, so the null-and-emit step is injected here; every other
   * teardown step is owned by this module.
   */
  clearInMemorySession: () => void;
  /** sessionStorage key for the ENG-138 signed-in-at marker, cleared by the wipe. */
  signedInAtStorageKey: string;
  /**
   * sessionStorage key oidc-client-ts persists the cached OIDC user under —
   * its `oidc.` WebStorageStateStore prefix + `user:<authority>:<client_id>`,
   * i.e. `oidc.user:${authBaseUrl}:${cognitoAppClientId}`. The wipe deletes
   * this SYNCHRONOUSLY (see wipeLocalSessionState). It is the ONE LOCAL
   * (sessionStorage) artifact that resurrects a session: client.ts's cold load
   * re-adopts it via `userManager.getUser()`, so it must not ride solely on the
   * deadline-raced `removeUser()`, which a hang or reject abandons. (The
   * auth-domain refresh cookie is the other, server-owned resurrection path; it
   * survives a failed /logout by design — see wipeLocalSessionState.)
   */
  oidcUserStorageKey: string;
}

/**
 * Build the client's `signOut`. Holds the sign-out-only state (the concurrency
 * memo) in this closure so it never leaks into client.ts.
 */
export function createSignOutFlow(
  deps: SignOutFlowDeps,
): (options?: SignOutOptions) => Promise<SignOutResult> {
  const {
    scheduler,
    authFetch,
    userManager,
    config,
    clearInMemorySession,
    signedInAtStorageKey,
    oidcUserStorageKey,
  } = deps;

  // ENG-2907 — one sign-out at a time. Concurrent signOut() calls share this
  // promise so they issue a SINGLE POST /logout (a second overlapping POST
  // after the first cleared the refresh cookie would no-op to 200 and hide the
  // real outcome). Mirrors coldLoadInflight; cleared in signOut()'s finally.
  let signOutInflight: Promise<SignOutResult> | null = null;
  let signOutFailureObservers:
    | Array<NonNullable<SignOutOptions["onFailureBeforeNavigate"]>>
    | null = null;

  function registerFailureObserver(
    observers: Array<NonNullable<SignOutOptions["onFailureBeforeNavigate"]>> | null,
    options?: SignOutOptions,
  ): void {
    if (!observers) return;
    try {
      const observer = options?.onFailureBeforeNavigate;
      if (observer) observers.push(observer);
    } catch {
      // Preserve signOut's never-throws contract even for a malformed options
      // object with an accessor that throws while the observer is registered.
    }
  }

  /** Deadline for the user-initiated POST /logout.
   *
   * Pre-ENG-2907 and the T3 narrowing both shipped this fetch with NO deadline,
   * so a /logout that never resolves (captive portal, wedged auth-service) left
   * signOut() pending forever: the local wipe never ran, the session stayed
   * fully live, and — because `signOutInflight` clears only in the IIFE's
   * `finally`, which a pending fetch never reaches — every subsequent click
   * returned the same forever-pending promise. Bounding it is strictly safer:
   * aborting a request the server may already have received is mostly harmless
   * (the server can still process it and we navigate regardless), whereas
   * hanging forever is the worst outcome on this path.
   *
   * 10s, NOT the deleted probe's 3s: a user-initiated logout POST is a
   * foreground request, not a background probe, and a slow-but-succeeding call
   * (cold Lambda + slow mobile network) is plausible — aborting one needlessly
   * buckets it as `network` and (in T8) triggers auto-retry. 10s sits at the
   * "user perceives broken" threshold with headroom for a slow success, and
   * well under API Gateway's 29s integration ceiling. Uses AbortController +
   * setTimeout, NOT AbortSignal.timeout (jsdom lacks it — see silent-refresh). */
  const SIGN_OUT_LOGOUT_TIMEOUT_MS = 10_000;

  /** Deadline for the local wipe's userManager.removeUser() (ENG-2943).
   *
   * INVARIANT — NO UNBOUNDED AWAIT MAY PRECEDE THE NAVIGATE TAIL. After #1767
   * bounded the /logout fetch, `await userManager.removeUser()` was the only
   * remaining unbounded await in signOut(). If it never settles the `try` never
   * completes, so the `catch` never runs, the `finally` never runs (the memo is
   * never cleared, so no retry is possible), and the navigation tail never runs
   * — the SPA is never torn down, which is the load-bearing defence against
   * credential resurrection (apollo-client.ts wires
   * `onUnauthenticated: () => void authClient.getAccessToken()`, and /refresh
   * rotates the refresh cookie). Racing removeUser() against this deadline keeps
   * the tail reachable. T8c adds the /userinfo probe in exactly this window and
   * MUST carry its own deadline for the same reason — a probe with no deadline
   * reintroduces this defect one await further down.
   *
   * 2s, far tighter than the 10s /logout deadline: removeUser() is a local
   * sessionStorage delete + a synchronous event, normally sub-millisecond — 2s
   * is >100x its realistic worst case (a wedged main thread / contended
   * storage backend) yet 5x tighter than the network POST, so it can neither
   * mask a slow-but-succeeding storage write nor perceptibly delay teardown on
   * the pathological hang. Same AbortController-free setTimeout race as above;
   * removeUser() takes no signal, so the timeout resolves and the wipe proceeds
   * with the removeUser() promise abandoned in the background. Post-MUST-FIX-1
   * that abandoned work is ONLY removeUser()'s userUnloaded event / internal
   * bookkeeping — the security-critical OIDC user record it would delete is
   * already removed synchronously by wipeLocalSessionState() BEFORE this race,
   * so nothing that can resurrect a session rides on the deadline. */
  const WIPE_REMOVE_USER_TIMEOUT_MS = 2_000;

  // ENG-2907 — the local session teardown, extracted so the outer catch can
  // guarantee it runs even on a PRE-WIPE throw. Idempotent: safe to call twice.
  // Today no pre-wipe statement is individually unguarded, but that is an
  // unenforced invariant, and T8 reintroduces pre-wipe code (the bearer /
  // sessionId capture) exactly where an unguarded throw would strand a live
  // session. Navigation is deliberately NOT part of this: it is a browser
  // side-effect with its own failure handling (navigateToPostLogout), and the
  // security-critical guarantee is that in-memory + persisted session state is
  // gone regardless of whether navigation succeeds.
  async function wipeLocalSessionState(): Promise<void> {
    clearInMemorySession();
    // ENG-2943 (MUST FIX 1) — delete the cached OIDC user record SYNCHRONOUSLY,
    // before racing removeUser() below. This record
    // (oidc.user:<authority>:<client_id>) is the ONE LOCAL (sessionStorage)
    // artifact that resurrects a session: client.ts's cold-load fast path
    // re-adopts it via userManager.getUser() and hands getAccessToken() back the
    // pre-sign-out token. removeUser() is the ONLY oidc-client-ts call that
    // deletes it — but the deadline below abandons a hung removeUser() in the
    // background, and a REJECTING one never reaches the delete either, so if the
    // delete rode on removeUser() alone, a hang or reject would leave the record
    // standing and the next cold load would resurrect the session (the
    // background /me probe is not a backstop — it returns synchronously first,
    // and only tears down on a body of exactly {"error":"invalid_token"}). Doing
    // the removeItem here, deadline-independent, closes the LOCAL path.
    // removeUser() still runs (below) for its userUnloaded event; the record is
    // already gone by the time it does.
    //
    // SCOPE — deleting this key does NOT close resurrection ENTIRELY, and a T8c
    // author must not read it that way: on a FAILED /logout the auth-domain
    // HTTP-only refresh COOKIE survives (we cannot clear it cross-origin), so the
    // next cold load can still re-mint a session via /me -> /refresh. That is the
    // DESIGNED `not_signed_out` contract (sign-out.ts), not a hole this delete
    // leaves — the local wipe stops THIS tab trusting the session; only a
    // server-confirmed /logout revokes the cookie. The local record and the
    // refresh cookie are the two resurrection paths; this line owns the first.
    if (typeof window !== "undefined") {
      try {
        window.sessionStorage.removeItem(oidcUserStorageKey);
      } catch {
        // best-effort cleanup only
      }
    }
    // ENG-2943 — deadline removeUser() (see WIPE_REMOVE_USER_TIMEOUT_MS). Race
    // it against a timeout that RESOLVES: a hung removeUser() must not strand the
    // wipe (and therefore the navigate tail). removeUser() takes no AbortSignal,
    // so a hang leaves its promise pending in the background; that is now
    // genuinely harmless — the security-critical OIDC user record was deleted
    // synchronously above, so this race abandons only removeUser()'s userUnloaded
    // event / internal bookkeeping. A REJECTING removeUser() lands in the catch,
    // as it did before the deadline (and, likewise, after the sync delete above).
    let removeUserTimer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        userManager.removeUser(),
        new Promise<void>((resolve) => {
          removeUserTimer = setTimeout(resolve, WIPE_REMOVE_USER_TIMEOUT_MS);
        }),
      ]);
    } catch {
      // ignore — sessionStorage cleanup
    } finally {
      // MUST FIX 2 — best-effort. A stubbed / instrumented clearTimeout that
      // throws (test shims, console-capturing RUM SDKs) must not skip the marker
      // cleanup below nor escape to the outer catch, which would re-run the wipe
      // and swallow the failure silently. Matches the removeItem convention
      // above. `!== undefined` (not truthiness): a valid timer handle can be 0.
      try {
        if (removeUserTimer !== undefined) clearTimeout(removeUserTimer);
      } catch {
        // best-effort — a leaked timer only resolves a race already decided
      }
    }
    if (typeof window !== "undefined") {
      try {
        window.sessionStorage.removeItem(signedInAtStorageKey);
      } catch {
        // best-effort cleanup only
      }
    }
  }

  // ENG-2907 — navigate to postLogoutUri, guarded. postLogoutUri is sanitised at
  // construction to a resolved http(s) URL, so this guard is defense-in-depth
  // against a `window.location.assign` that still throws (a test double, or a
  // browser that refuses the navigation). Tearing the SPA down is
  // load-bearing (it kills any in-flight caller — e.g. Apollo's
  // onUnauthenticated re-mint — that could reinstall the credential we just
  // declared dead), so a throwing assign must NOT abort the sign-out or corrupt
  // the verdict. Fall back assign -> replace -> href; only if the LAST also
  // throws does it propagate, to the outer catch, which returns the
  // already-computed server verdict (never `unexpected`) and re-runs the
  // idempotent wipe. A local navigation failure must never overwrite a server
  // outcome.
  function navigateToPostLogout(): void {
    if (typeof window === "undefined") return;
    const uri = config.postLogoutUri;
    try {
      window.location.assign(uri);
      return;
    } catch (err) {
      safeWarn("[auth-client] sign-out assign() navigation threw; trying replace()", err);
    }
    try {
      window.location.replace(uri);
      return;
    } catch (err) {
      safeWarn("[auth-client] sign-out replace() navigation threw; trying href", err);
    }
    window.location.href = uri;
  }

  async function signOut(options?: SignOutOptions): Promise<SignOutResult> {
    // Concurrency memo: one POST /logout even under overlapping calls. Mirrors
    // coldLoadInflight; the IIFE clears it in `finally`. Joining callers still
    // register their own pre-navigation observers on the active flow so an idle
    // timeout that joins a user-initiated sign-out cannot lose its failure
    // signal merely because it did not create the shared promise.
    if (signOutInflight) {
      registerFailureObserver(signOutFailureObservers, options);
      return signOutInflight;
    }
    const failureObservers: Array<
      NonNullable<SignOutOptions["onFailureBeforeNavigate"]>
    > = [];
    registerFailureObserver(failureObservers, options);
    signOutFailureObservers = failureObservers;
    signOutInflight = (async (): Promise<SignOutResult> => {
      // Hoisted ABOVE the try so the outer catch can return the SERVER's verdict
      // even when a LOCAL step after it (navigation) throws. A local navigation
      // failure must not overwrite a real /logout outcome with `unexpected` —
      // that both reports a successful sign-out as a failure and pollutes T8's
      // alarm, which keys on the RATE of `unexpected` (sign-out.ts).
      let result: SignOutResult | undefined;
      try {
        // scheduler.cancel() is best-effort and MUST NOT skip the wipe: guard it
        // on its own so a throw here still tears down local state below
        // (ENG-2907 F3 — otherwise a throwing cancel would leave the session
        // cached and re-adoptable on the next load).
        try {
          scheduler.cancel();
        } catch (err) {
          // best-effort — the wipe below is what stops the UI trusting the
          // session; log so a broken cancel is not fully silent.
          safeWarn("[auth-client] scheduler.cancel() during sign-out threw", err);
        }

        // POST /logout, DEADLINED (see SIGN_OUT_LOGOUT_TIMEOUT_MS). Classify
        // STATUS ONLY. `network` comes ONLY from an actual fetch rejection (the
        // `{ error }` branch — an abort on the deadline lands here and maps to
        // the retriable `network` bucket); a resolved-but-garbage response is a
        // client bug -> `unexpected` (see sign-out.ts).
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), SIGN_OUT_LOGOUT_TIMEOUT_MS);
        try {
          const response = await authFetch("/logout", {
            method: "POST",
            signal: controller.signal,
          });
          result = classifySignOutResponse({ response });
        } catch (error) {
          result = classifySignOutResponse({ error });
        } finally {
          // MUST FIX 2 — best-effort, see the wipe timer's clearTimeout guard.
          // A throwing clearTimeout here would otherwise reach the outer catch;
          // the wipe still runs there, but the guard keeps the failure from
          // silently reshaping the control flow (and shields T8c's additions).
          try {
            clearTimeout(timer);
          } catch {
            // best-effort — the deadline only aborts a fetch already settled
          }
        }

        // Local wipe — UNCONDITIONAL, on EVERY path including failure. This is
        // the behaviour that must never regress: the UI stops trusting the old
        // session regardless of what /logout said.
        await wipeLocalSessionState();
      } catch (err) {
        // Contract (this interface's doc + sign-out.ts): signOut() NEVER
        // rejects. Any unforeseen throw resolves. Run the (idempotent) wipe here
        // too so a PRE-WIPE throw can never strand a live session (T8
        // reintroduces pre-wipe code). PRESERVE a verdict already computed before
        // the throw with `??=` (the HOIST) rather than clobbering it to
        // `unexpected`.
        //
        // In T3 NO single reachable throw site lands here with `result` already
        // classified: F1/F2 made wipeLocalSessionState() internally total and
        // CONTAINED both clearTimeout calls, and the navigate is a common tail
        // OUTSIDE this try. So `??=` reads as dead in isolation — ablating it to
        // `=` is 178/178 green. It is NOT dead: a single green ablation here is
        // evidence of REDUNDANCY, not of deadness. `??=` and the /logout-deadline
        // clearTimeout guard are SUBSTITUTES against one hazard — a throwing
        // clearTimeout in the /logout `finally` escaping into this catch AFTER
        // classification and masking a real `signed_out` as `unexpected`. Remove
        // EITHER and the suite stays green; remove BOTH and it goes red. Order-2
        // mutation testing demonstrates the pair is jointly load-bearing (it is
        // not a bare claim — reproduce it):
        //     /logout clearTimeout guard removed  -> green
        //     `??=` -> `=`                         -> green
        //     BOTH removed (double mutant)         -> RED at the VERDICT assertion
        //       of client.signout.test.ts's "a THROWING clearTimeout is CONTAINED
        //       (MUST FIX 2)" test:
        //         expected { verdict: 'not_signed_out', reason: 'unexpected' }
        //              to deeply equal { verdict: 'signed_out', probe: 'not_run' }
        // (Removing the WIPE clearTimeout guard alone is a DIFFERENT hazard and
        // reddens that SAME test's MARKER assertion instead — see that guard's
        // comment.) T8c's pre-navigate /userinfo await reintroduces a
        // post-classification throw site here, restoring SINGLE-mutant
        // reachability so `??=` stands alone again — do not delete it before then.
        safeWarn("[auth-client] sign-out hit an unexpected error", err);
        try {
          await wipeLocalSessionState();
        } catch {
          // best-effort — nothing else can be done
        }
        result ??= { verdict: "not_signed_out", reason: "unexpected" };
      } finally {
        signOutInflight = null;
        signOutFailureObservers = null;
      }
      // Navigate — GUARDED COMMON TAIL, reached on EVERY path (FINDING 1). It was
      // inside the try, so a PRE-navigate throw (the AbortController/setTimeout
      // construction, or T8's pre-navigate code) hit the catch and returned with
      // the SPA still alive. Tearing the SPA down is the load-bearing safety
      // property: it kills any in-flight caller — e.g. Apollo onUnauthenticated
      // re-mint — that could reinstall the credential the wipe just declared
      // dead. Its own try means a navigation failure cannot reject signOut() or
      // undo the memo clear above; `?? unexpected` is the type-guard for a path
      // that reaches here with no verdict (the catch's `??=` already backstops it
      // at runtime, but TS cannot prove `result` defined after the try/catch).
      //
      // SINGLE-FIRE gating is DEFERRED to T8c (T8C-NAV-GATE). In T3 there is NO
      // await between the memo clear in `finally` and the navigate, so it fires
      // exactly once per flow. The ENG-2954 failure observers DO run in that
      // window — synchronously, after the memo clear and before navigation — so
      // caller code executes there; it is contained (per-observer try/catch,
      // frozen copies) and bound by the onFailureBeforeNavigate contract
      // (synchronous, cheap, never re-entering signOut()). An earlier
      // module-closure `signOutTerminal` flag re-armed at signOut() ENTRY was
      // removed here: its lifecycles were mismatched (armed at entry, consumed
      // at the tail), so once T8c inserts the /userinfo await BEFORE this tail,
      // an entry-keyed flag can suppress a navigation that SHOULD happen —
      // flow 2's tail consuming flow 1's arm. T8c must gate PER-FLOW (a local
      // `let navigated = false` inside this IIFE, or a flow-generation token
      // compared here) so the guard is both live and provable in the same
      // commit that adds the await it defends.
      const terminalResult = result ?? {
        verdict: "not_signed_out",
        reason: "unexpected",
      };
      if (terminalResult.verdict === "not_signed_out") {
        for (const observeFailure of failureObservers) {
          try {
            // A fresh frozen copy per observer: a hook cannot rewrite the
            // server verdict signOut() returns, nor what the next hook sees.
            observeFailure(Object.freeze({ ...terminalResult }));
          } catch {
            // Each observer is isolated so one broken telemetry sink cannot
            // suppress another or interfere with teardown/navigation.
          }
        }
      }
      try {
        navigateToPostLogout();
      } catch (err) {
        safeWarn("[auth-client] sign-out navigation threw after teardown", err);
      }
      return terminalResult;
    })();
    return signOutInflight;
  }

  return signOut;
}
